import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Alert, Box, Button, CircularProgress, IconButton, InputAdornment, Link, Stack, TextField, Typography } from '@mui/material';
import axios from 'axios';
import React from 'react';
import { Link as RouterLink, useHistory, useLocation } from 'react-router-dom';
import { IRegister, LoginApi } from '../api/LoginApi';
import { UserApi } from '../api/UserApi';
import { isJwtLoggedIn } from '../util/session';
import VerbaScholaAccountCard, { accountBodySx, accountLinkSx } from '../component/VerbaScholaAccountCard';
import { LS_PENDING_SIGNUP_REFERRER_SENTENCE_SCORE_ID, LS_PUBLIC_SCORE_IMPORT_ID } from '../referrerStorage';
import { ActivityEnum, useUserActivityTracker } from '../UserActivityTracker';
import { BLUE_TEXT, INTER, PILOT_REQUEST_HREF, SLATE, SOFT_BLUE, primaryButtonSx } from '../style/verbaScholaPublicStyles';

// 'link' errors come from the invitation or class-code link itself, so resubmitting cannot help.
type SignupError = { kind: 'exists' } | { kind: 'link' | 'message'; text: string };

// Server registration errors (UserController.register) mapped to plain-language messages.
function signupErrorFromResponse(status: number | undefined, message: string | undefined): SignupError {
    if (status === 495) return { kind: 'exists' };
    if (status === 498 && /class code|signup code/i.test(message ?? '')) {
        return { kind: 'link', text: 'This class code isn’t valid. Check the link from your teacher, or ask them to send it again.' };
    }
    if (status === 498) {
        return { kind: 'link', text: 'This invitation link isn’t valid. Ask the person who invited you to send it again, or log in if you already set up your account.' };
    }
    if (status === 497) return { kind: 'message', text: 'Enter a valid email address.' };
    if (status === 494) return { kind: 'message', text: 'The passwords don’t match. Enter the same password twice.' };
    return { kind: 'message', text: message || 'We couldn’t create your account right now. Try again in a moment.' };
}

export default function Registration() {
    useUserActivityTracker(ActivityEnum.Settings, null, null, true);
    const history = useHistory();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const studentInviteToken = params.get('invite');
    const invitedEmail = studentInviteToken ? params.get('email') ?? '' : '';
    // A class code applies only to the signup link that carries it, never to a later visit.
    const courseSignupCode = params.get('c');

    const [firstName, setFirstName] = React.useState('');
    const [lastName, setLastName] = React.useState('');
    const [email, setEmail] = React.useState(invitedEmail);
    const [password, setPassword] = React.useState('');
    const [confirmPassword, setConfirmPassword] = React.useState('');
    const [showPassword, setShowPassword] = React.useState(false);
    const [mismatch, setMismatch] = React.useState(false);
    const [error, setError] = React.useState<SignupError | null>(null);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [createdEmail, setCreatedEmail] = React.useState<string | null>(null);
    const confirmRef = React.useRef<HTMLInputElement>(null);
    const errorRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        // Earlier versions saved class codes across visits; clear any leftover.
        localStorage.removeItem('c');
    }, []);

    React.useEffect(() => {
        if (!isJwtLoggedIn()) return;
        let active = true;
        UserApi.get()
            .then(response => {
                if (active) history.replace(response.data.role === 'teacher' || response.data.role === 'admin' ? '/teacher' : '/student/settings');
            })
            .catch(() => { if (active) history.replace('/login'); });
        return () => { active = false; };
    }, [history]);

    React.useEffect(() => {
        if (error) errorRef.current?.focus();
    }, [error]);

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (isSubmitting) return;
        setError(null);
        if (password !== confirmPassword) {
            setMismatch(true);
            confirmRef.current?.focus();
            return;
        }
        const registerData: IRegister = {
            email,
            password,
            confirmPassword,
            firstName,
            lastName,
            referrerSentenceScoreId: localStorage.getItem(LS_PENDING_SIGNUP_REFERRER_SENTENCE_SCORE_ID) ?? localStorage.getItem(LS_PUBLIC_SCORE_IMPORT_ID),
            courseSignupCode,
            studentInviteToken,
        };
        setIsSubmitting(true);
        LoginApi.register(registerData)
            .then(response => {
                if (response.status === 201) {
                    localStorage.removeItem(LS_PENDING_SIGNUP_REFERRER_SENTENCE_SCORE_ID);
                    setCreatedEmail(email);
                } else {
                    // The API answers 200 without creating an account when the email is taken.
                    setError({ kind: 'exists' });
                }
            })
            .catch(err => {
                const status = axios.isAxiosError(err) ? err.response?.status : undefined;
                const message = axios.isAxiosError(err) ? err.response?.data?.message : undefined;
                setError(signupErrorFromResponse(status, typeof message === 'string' ? message : undefined));
            })
            .finally(() => setIsSubmitting(false));
    };

    if (isJwtLoggedIn()) return null;

    if (createdEmail !== null) {
        return (
            <VerbaScholaAccountCard title="Check your email">
                <Typography sx={{ ...accountBodySx, mt: 2 }}>
                    Your account is ready. We sent a link to <Box component="strong" sx={{ wordBreak: 'break-all' }}>{createdEmail}</Box> to
                    confirm your email address. It can take up to 30 minutes, so check your spam folder too.
                </Typography>
                <Typography sx={{ ...accountBodySx, mt: 1.5 }}>
                    You don&apos;t have to wait: you can log in now and confirm your email later.
                </Typography>
                <Button component={RouterLink} to="/login" sx={{ ...primaryButtonSx, minHeight: 48, mt: 3, width: '100%' }}>Log in</Button>
            </VerbaScholaAccountCard>
        );
    }

    const passwordAdornment = (
        <InputAdornment position="end">
            <IconButton onClick={() => setShowPassword(value => !value)} edge="end"
                aria-label={showPassword ? 'Hide passwords' : 'Show passwords'} aria-pressed={showPassword}>
                {showPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
            </IconButton>
        </InputAdornment>
    );
    const errorId = error ? 'signup-error' : undefined;

    return (
        <VerbaScholaAccountCard width={480}
            title={invitedEmail ? 'Set up your account' : 'Create a student account'}
            intro={invitedEmail
                ? 'You’ve been invited to a class on VerbaSchola. Choose a password to get started.'
                : courseSignupCode
                    ? 'Create your account to join the class.'
                    : 'If you got an invitation email, use its link instead so you join the right class.'}
            // An invitation may be for a co-teacher, so the teacher pilot hint is only for uninvited visitors.
            footer={invitedEmail ? undefined : <>Teachers: <Link component={RouterLink} to={PILOT_REQUEST_HREF} underline="hover" sx={{ ...accountLinkSx, fontSize: 13 }}>request a pilot</Link> to get a teacher account.</>}>
            {courseSignupCode && !error && (
                <Box sx={{ mt: 2.5, px: 2, py: 1.5, borderRadius: '12px', backgroundColor: SOFT_BLUE }}>
                    <Typography sx={{ fontFamily: INTER, fontSize: 14, color: BLUE_TEXT, wordBreak: 'break-all' }}>
                        Joining with class code <Box component="strong" sx={{ letterSpacing: '0.04em' }}>{courseSignupCode}</Box>
                    </Typography>
                </Box>
            )}
            {error && (
                <Alert severity="error" ref={errorRef} tabIndex={-1} id="signup-error" sx={{ mt: 2.5, borderRadius: '12px', outline: 'none' }}>
                    {error.kind === 'exists'
                        ? <>An account already uses this email. <Link component={RouterLink} to="/login" sx={{ fontWeight: 700, color: 'inherit' }}>Log in</Link> or <Link component={RouterLink} to="/reset_password_email" sx={{ fontWeight: 700, color: 'inherit' }}>reset your password</Link>.</>
                        : error.text}
                </Alert>
            )}
            <Stack component="form" spacing={2} onSubmit={handleSubmit} sx={{ mt: 3 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField id="firstName" label="First name" name="firstName" autoComplete="given-name" required fullWidth
                        value={firstName} onChange={event => setFirstName(event.target.value)} />
                    <TextField id="lastName" label="Last name" name="lastName" autoComplete="family-name" required fullWidth
                        value={lastName} onChange={event => setLastName(event.target.value)} />
                </Stack>
                <TextField id="email" label="Email address" name="email" type="email" autoComplete="email" required fullWidth
                    value={email} onChange={event => { setEmail(event.target.value); if (error?.kind === 'exists') setError(null); }}
                    error={error?.kind === 'exists'}
                    InputProps={{ readOnly: Boolean(invitedEmail) }}
                    inputProps={{ 'aria-describedby': error?.kind === 'exists' ? errorId : undefined }}
                    helperText={invitedEmail ? 'This is the address you were invited with.' : undefined} />
                <TextField id="password" label="New password" name="password" type={showPassword ? 'text' : 'password'} autoComplete="new-password" required fullWidth
                    value={password} onChange={event => { setPassword(event.target.value); setMismatch(false); }}
                    InputProps={{ endAdornment: passwordAdornment }} />
                <TextField id="confirmPassword" label="Confirm new password" name="confirmPassword" type={showPassword ? 'text' : 'password'} autoComplete="new-password" required fullWidth
                    value={confirmPassword} onChange={event => { setConfirmPassword(event.target.value); setMismatch(false); }}
                    error={mismatch} helperText={mismatch ? 'The passwords don’t match. Enter the same password twice.' : undefined} inputRef={confirmRef} />
                <Button type="submit" disabled={isSubmitting || error?.kind === 'link'} sx={{ ...primaryButtonSx, minHeight: 48 }}>
                    {isSubmitting
                        ? <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={18} sx={{ color: 'inherit' }} aria-hidden /><span>Creating account…</span></Stack>
                        : invitedEmail ? 'Set up account' : 'Create account'}
                </Button>
            </Stack>
            <Typography sx={{ fontFamily: INTER, fontSize: 14, color: SLATE, textAlign: 'center', mt: 3 }}>
                Already have an account?{' '}
                <Link component={RouterLink} to="/login" underline="hover" sx={accountLinkSx}>Log in</Link>
            </Typography>
        </VerbaScholaAccountCard>
    );
}
